import { useSearchParams, Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';

import {
  useEdiIntakeList,
  HOLD_REASON_LABELS,
  STATUS_LABELS,
  type EdiIntakeRow,
} from '@/hooks/admin/useEdi';
import { cn } from '@/lib/utils';

/**
 * /admin/edi — the EDI email-order review queue. Every PO emailed to an EDI
 * inbound address lands here; held POs (review-first, unmatched lines,
 * unknown store, duplicates, failures) wait for a human, submitted ones show
 * their order. Detail/decisions live at /admin/edi/:intakeId.
 */

const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'needs_review', label: 'Needs review' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'failed', label: 'Failed' },
  { key: 'all', label: 'All' },
];

function statusBadge(row: EdiIntakeRow) {
  const base = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold';
  switch (row.status) {
    case 'submitted':
      return <span className={cn(base, 'bg-emerald-100 text-emerald-700')}>Submitted</span>;
    case 'needs_review':
      return (
        <span className={cn(base, 'bg-amber-100 text-amber-700')}>
          {HOLD_REASON_LABELS[row.hold_reason ?? ''] ?? 'Needs review'}
        </span>
      );
    case 'rejected':
      return <span className={cn(base, 'bg-slate-200 text-slate-600')}>Rejected</span>;
    case 'failed':
      return <span className={cn(base, 'bg-red-100 text-red-700')}>Failed</span>;
    default:
      return <span className={cn(base, 'bg-slate-100 text-slate-600')}>{STATUS_LABELS[row.status]}</span>;
  }
}

export default function AdminEdi() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'needs_review';
  const customerId = params.get('customer') ?? undefined;

  const { data, isLoading } = useEdiIntakeList({ status, customerId });
  const rows = data?.rows ?? [];

  return (
    <div className="px-6 py-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-semibold text-brix-ink flex items-center gap-2">
            <Inbox size={16} className="text-brix-primary" />
            EDI orders — emailed POs
          </h2>
          <p className="text-sm text-brix-muted mt-0.5 max-w-2xl">
            Purchase orders emailed to an EDI inbound address. Held POs need a review
            before they become a Service Fusion order; the customer's EDI settings live
            on their Billing &amp; comms tab.
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('status', f.key);
                setParams(next, { replace: true });
              }}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                status === f.key
                  ? 'bg-brix-primary text-white'
                  : 'bg-brix-surface border border-brix-border text-brix-muted hover:text-brix-ink',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {customerId && (
        <div className="text-xs text-brix-muted">
          Filtered to one customer.{' '}
          <button
            type="button"
            className="text-brix-primary font-semibold hover:underline"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete('customer');
              setParams(next, { replace: true });
            }}
          >
            Show all customers
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-brix-muted py-10 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brix-border py-14 text-center text-sm text-brix-muted">
          {status === 'needs_review'
            ? 'Nothing waiting for review.'
            : 'No emailed POs match this filter yet.'}
        </div>
      ) : (
        <div className="rounded-xl border border-brix-border bg-brix-surface divide-y divide-brix-border overflow-hidden">
          {rows.map((row) => {
            const lines = row.matched?.length ?? row.parsed?.lines.length ?? 0;
            const unmatched = row.matched?.filter((m) => !m.catalog_item_id).length ?? 0;
            return (
              <Link
                key={row.id}
                to={`/admin/edi/${row.id}`}
                className="flex items-center gap-4 px-4 py-3 hover:bg-brix-primary-tint/30 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-brix-ink truncate">
                      {row.customer_name ?? 'Unknown customer'}
                    </span>
                    {row.po_number && (
                      <span className="text-xs font-mono text-brix-muted">PO {row.po_number}</span>
                    )}
                    {row.parsed?.store_name && (
                      <span className="text-xs text-brix-muted truncate">· {row.parsed.store_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-brix-muted mt-0.5 truncate">
                    {row.from_email ?? 'unknown sender'}
                    {row.subject ? ` · ${row.subject}` : ''}
                    {lines > 0 && ` · ${lines} line${lines === 1 ? '' : 's'}`}
                    {unmatched > 0 && ` (${unmatched} unmatched)`}
                  </div>
                </div>
                <div className="shrink-0 text-right space-y-1">
                  {statusBadge(row)}
                  <div className="text-[11px] text-brix-muted">
                    {new Date(row.received_at ?? row.created_at).toLocaleString()}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
